import { z } from "zod";
import { parentKindIndexSchema } from "~/domains/nachlass/erbschein/shared/erbfolgeSchemas";
import { createDateInputSchema } from "~/services/validation/dateObject";
import { hiddenInputSchema } from "~/services/validation/hiddenInput";
import { schemaOrEmptyStringOptional } from "~/services/validation/schemaOrEmptyString";
import { stringOptionalSchema } from "~/services/validation/stringOptional";
import { stringRequiredSchema } from "~/services/validation/stringRequired";
import { YesNoAnswer } from "~/services/validation/YesNoAnswer";
import { addYears, today } from "~/util/date";

const dateInputSchema = createDateInputSchema({
  earliest: () => addYears(today(), -150),
  latest: () => today(),
});

const commonPersonFields = {
  vorname: stringRequiredSchema,
  nachname: stringRequiredSchema,
  geburtsname: stringOptionalSchema,
  geburtsdatum: dateInputSchema,
  geburtsort: stringRequiredSchema,
  isAlive: YesNoAnswer,
};

const deceasedPersonFields = {
  isAlive: z.literal("no"),
  sterbedatum: dateInputSchema,
  sterbeort: stringRequiredSchema,
};

const alivePersonFields = {
  isAlive: z.literal("yes"),
  strasse: stringRequiredSchema,
  hausnummer: stringRequiredSchema,
  plz: stringRequiredSchema,
  ort: stringRequiredSchema,
  land: stringRequiredSchema,
  adresszusatz: stringOptionalSchema,
};

export const alivePersonSchema = z.object({
  ...commonPersonFields,
  ...alivePersonFields,
});

export const deceasedPersonNoKidsSchema = z.object({
  ...commonPersonFields,
  ...deceasedPersonFields,
  hatteKinder: z.literal("no"),
});

export function personUnion<Child extends z.ZodTypeAny>(childSchema: Child) {
  return z.union([
    alivePersonSchema,
    deceasedPersonNoKidsSchema,
    z.object({
      ...commonPersonFields,
      ...deceasedPersonFields,
      hatteKinder: z.literal("yes"),
      kinder: z.array(childSchema).optional(),
    }),
  ]);
}

//#region field helpers
export const nameFieldsHelper = (prefix: string) => ({
  [`${prefix}vorname`]: stringRequiredSchema,
  [`${prefix}nachname`]: stringRequiredSchema,
  [`${prefix}geburtsname`]: stringOptionalSchema,
});

export const geburtsdatumFieldsHelper = (prefix: string) => ({
  [`${prefix}geburtsdatum`]: dateInputSchema,
  [`${prefix}sterbedatum`]: hiddenInputSchema(
    schemaOrEmptyStringOptional(dateInputSchema),
  ),
  [`${prefix}geburtsort`]: stringRequiredSchema,
});

export const parentKindIndexFieldHelper = (prefix: string, depth: number) => {
  if (depth < 2) return {};
  return {
    [`${prefix}parentKindIndex`]: parentKindIndexSchema,
  };
};

export const addressFieldsHelper = (prefix: string) => ({
  [`${prefix}strasse`]: stringRequiredSchema,
  [`${prefix}hausnummer`]: stringRequiredSchema,
  [`${prefix}plz`]: stringRequiredSchema,
  [`${prefix}ort`]: stringRequiredSchema,
  [`${prefix}land`]: stringRequiredSchema,
  [`${prefix}adresszusatz`]: stringOptionalSchema,
});

export const deathDateFieldsHelper = (prefix: string) => ({
  [`${prefix}sterbedatum`]: dateInputSchema,
  [`${prefix}geburtsdatum`]: hiddenInputSchema(dateInputSchema),
  [`${prefix}sterbeort`]: stringRequiredSchema,
});

export const isAliveFieldHelper = (prefix: string) => ({
  [`${prefix}isAlive`]: YesNoAnswer,
});

export const hatteKinderFieldHelper = (prefix: string) => ({
  [`${prefix}hatteKinder`]: YesNoAnswer,
});
//#endregion
